import {NormalizedPromotion, NormalizedPromotionGroup} from '../../store/PromotionStore'
import {MutableRefObject} from 'react'
import {makeAutoObservable, observable, ObservableMap} from 'mobx'

export class MealDealBuilderState {
  id: string
  groups: MealDealBuilderGroupState[]

  constructor(promotion: NormalizedPromotion) {
    this.id = promotion.id
    this.groups = []

    promotion.groups.forEach((group) => {
      this.groups.push(new MealDealBuilderGroupState(group))
    })

    makeAutoObservable(this, {}, {autoBind: true})
  }

  /**
   * Check if all groups in the meal deal builder are complete
   * @returns true if complete; else false
   */
  get isComplete() {
    return this.groups.every((group) => group.isComplete)
  }

  /**
   * Returns an array of selected product ids with their quantities for the deal.
   * @returns An array of objects containing productId and quantity for each selected product in the deal
   */
  get selectedProducts() {
    const selectedProducts: {productId: string; quantity: number}[] = []
    this.groups.forEach((group) => {
      group.productItems.forEach((quantity, productId) => {
        selectedProducts.push({productId, quantity})
      })
    })
    return selectedProducts
  }

  /**
   * Returns amount of steps remaining for the user to complete the meal deal based on the groups in the builder.
   * @returns number of steps remaining to complete the meal deal
   */
  get stepsRemaining() {
    let steps = 0
    this.groups.forEach((group) => {
      if (!group.isComplete) {
        steps += 1
      }
    })
    return steps
  }

  /**
   * Returns which groups are incomplete to automatically navigate the user to the next incomplete step in the meal deal builder.
   * @returns An array of incomplete groups in the builder
   */
  get incompleteGroups() {
    const incompleteGroups: number[] = []
    this.groups.forEach((group) => {
      if (!group.isComplete) {
        incompleteGroups.push(this.groups.indexOf(group))
      }
    })
    return incompleteGroups
  }

  /**
   * Return total quantity of products selected across all groups
   * @returns total selected quantity as number
   */
  get totalSelected() {
    return this.groups.reduce((total, group) => total + group.totalSelected, 0)
  }
}

/**
 * Meal deal builder group class to manage the state of a group in the meal deal builder.
 * It tracks the selected products and their quantities, as well as the required quantity for the group.
 */
export class MealDealBuilderGroupState {
  productItems: ObservableMap<string, number>
  requiredProductQuantity: number
  allowedProductIds: string[]

  constructor(promotionGroup: NormalizedPromotionGroup) {
    this.productItems = observable.map<string, number>()
    this.requiredProductQuantity = Number(promotionGroup.req) || 0
    this.allowedProductIds = promotionGroup.ids || []

    makeAutoObservable(this, {}, {autoBind: true})
  }

  clearSelectionsExcept(productId: string) {
    this.allowedProductIds.forEach((allowedProductId) => {
      if (allowedProductId !== productId) {
        this.productItems.delete(allowedProductId)
      }
    })
  }

  /**
   * Check if the group is complete
   * @returns true if complete; else false
   */
  get isComplete(): boolean {
    return this.remainingQuantity === 0
  }

  /**
   * Update the selected quantity for a product in the group.
   * @param productId Product id to update the quantity for
   * @param quantity Quantity to set for the product
   * @returns The updated quantity for the product, or undefined if the product is not part of the group
   */
  updateProductSelection(productId: string, quantity: number) {
    if (!this.allowedProductIds.includes(productId)) {
      return
    }

    if (quantity <= 0) {
      this.productItems.delete(productId)
      return 0
    }

    const selectedProductQuantity = this.productItems.get(productId) || 0
    const groupTotalSelected = this.totalSelected - selectedProductQuantity

    // If the new quantity would put the group over the required quantity, do not update
    // and return the current selected quantity for the product.
    if (groupTotalSelected + quantity > this.requiredProductQuantity) {
      return selectedProductQuantity
    }

    this.productItems.set(productId, quantity)

    return quantity
  }

  /**
   * Return total quantity of products selected in the group
   * @returns total selected quantity as number
   */
  get totalSelected() {
    let totalSelected = 0
    this.productItems.forEach((selectedQuantity) => {
      totalSelected += selectedQuantity ?? 0
    })
    return totalSelected
  }

  /**
   * Return remaining quantity of products to be selected to complete the group
   * @returns remaining quantity as number
   */
  get remainingQuantity() {
    return this.requiredProductQuantity - this.totalSelected
  }
}

// Clears all product selections within the builder ref element
const clearProductSelections = (builderRef?: MutableRefObject<HTMLElement | null>) => {
  const selection =
    typeof document !== 'undefined' && document.getSelection ? document.getSelection() : null

  if (!selection?.rangeCount || !builderRef?.current) {
    return
  }

  const nodes = [selection.anchorNode, selection.focusNode]
  if (nodes.some((node) => node && builderRef.current?.contains(node))) {
    selection.removeAllRanges()
  }
}

// Resets the builder state, selections, and scrolls back to top
export const resetBuilder = (
  promotion: NormalizedPromotion,
  setBuilderState: (state: MealDealBuilderState | null) => void,
  update: (index: number, complete: boolean) => void,
  builderRef?: MutableRefObject<HTMLElement | null>
) => {
  if (!promotion) {
    return
  }

  const newState = new MealDealBuilderState(promotion)
  setBuilderState(newState)

  promotion?.groups?.forEach((_, index) => update(index, false))

  clearProductSelections(builderRef)
}
